import * as ToastPrimitive from '@radix-ui/react-toast';
import * as React from 'react';
import {
  faCheck,
  faCircleExclamation,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { ToastOptions } from 'common-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Text } from '..';
import { keyframes, styled, theme } from '../stitches.config';
import { FAIcon } from './icons/fa-icon';

const VIEWPORT_PADDING = 25;
const ANIMATION_TIME = 200;

const slideInBottom = keyframes({
  from: { transform: `translateY(calc(100% + ${VIEWPORT_PADDING}px))` },
  to: { transform: 'translateY(0)' },
});

const slideOutBottom = keyframes({
  from: { transform: 'translateY(0)' },
  to: { transform: `translateY(calc(100% + ${VIEWPORT_PADDING}px))` },
});

const slideInTop = keyframes({
  from: { transform: `translateY(calc(-100% - ${VIEWPORT_PADDING}px))` },
  to: { transform: 'translateY(0)' },
});

const slideOutTop = keyframes({
  from: { transform: 'translateY(0)' },
  to: { transform: `translateY(calc(-100% - ${VIEWPORT_PADDING}px))` },
});

const swipeOut = keyframes({
  from: { transform: 'translateX(var(--radix-toast-swipe-end-x))' },
  to: { transform: `translateX(calc(100% + ${VIEWPORT_PADDING}px))` },
});

const defaultContent = {
  info: {
    icon: <FAIcon size="lg" icon={faInfoCircle} />,
    title: 'Did you know?',
  },
  success: {
    icon: <FAIcon size="lg" icon={faCheck} />,
    title: 'Yay! Everything worked!',
  },
  danger: {
    icon: <FAIcon size="lg" icon={faCircleExclamation} />,
    title: 'Something went wrong, please try again.',
  },
  warning: {
    icon: <FAIcon size="lg" icon={faInfoCircle} />,
    title: 'Just a heads up!',
  },
};

type Context = {
  showToast: (options: ToastOptions) => void;
  closeToast: () => void;
};

const ToasterContext = createContext<Context | undefined>(undefined);

type ToasterProviderProps = {
  isMobileWeb?: boolean;
};

const ToasterProvider: React.FC<
  React.PropsWithChildren<ToasterProviderProps>
> = ({ children, isMobileWeb = false }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<'bottom' | 'top'>('bottom');
  const [toast, setToast] = useState<ToastOptions>({ color: 'info' });
  const timerRef = useRef(0);

  const showToast = useCallback((options: ToastOptions) => {
    setOpen(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setToast(options);
    timerRef.current = window.setTimeout(() => {
      setOpen(true);
    }, ANIMATION_TIME);
  }, []);

  const closeToast = useCallback(() => {
    setOpen(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);
  const value = useMemo(() => ({ showToast, closeToast }), [closeToast]);

  const titleIsComponent = typeof toast.title === 'function';
  const descriptionIsComponent = typeof toast.description === 'function';
  const icon = toast.icon ?? defaultContent[toast.color]?.icon;
  useEffect(() => {
    if (isMobileWeb) {
      setPosition('top');
    } else {
      setPosition('bottom');
    }
  }, [isMobileWeb]);

  return (
    <ToasterContext.Provider value={value}>
      <ToastPrimitive.Provider>
        {children}
        <Toast
          color={toast.color}
          open={open}
          onOpenChange={setOpen}
          role="listitem"
          position={position}
          duration={toast.duration}
        >
          <ToastTitle color={toast?.color} asChild={titleIsComponent}>
            {titleIsComponent ? (
              toast.title
            ) : (
              <>
                <Text css={{ flexShrink: 0 }}>{icon}</Text>
                {toast.title ?? defaultContent[toast.color]?.title}
              </>
            )}
          </ToastTitle>
          {toast.description && (
            <ToastDescription
              color={toast.color}
              asChild={descriptionIsComponent}
            >
              {toast.description}
            </ToastDescription>
          )}
        </Toast>
        <ToastViewport style={toast?.style} position={position} />
      </ToastPrimitive.Provider>
    </ToasterContext.Provider>
  );
};

const useToaster = () => {
  const context = useContext(ToasterContext);
  if (context === undefined) {
    throw new Error('useToaster must be used within a ToasterProvider');
  }
  return context;
};

export { ToasterProvider, useToaster };

const Toast = styled(ToastPrimitive.Root, {
  variants: {
    color: {
      info: {
        backgroundColor: theme.colors.blue2,
        borderColor: theme.colors.blue6,
        $$shadowColor: theme.colors.blueAlpha7,
      },
      success: {
        backgroundColor: theme.colors.green2,
        borderColor: theme.colors.green6,
        $$shadowColor: theme.colors.greenAlpha7,
      },
      danger: {
        backgroundColor: theme.colors.red2,
        borderColor: theme.colors.red6,
        $$shadowColor: theme.colors.redAlpha7,
      },
      warning: {
        backgroundColor: theme.colors.yellow2,
        borderColor: theme.colors.yellow6,
        $$shadowColor: theme.colors.yellowAlpha7,
      },
    },
    position: {
      bottom: {
        '@media (prefers-reduced-motion: no-preference)': {
          '&[data-state="open"]': {
            animation: `${slideInBottom} ${ANIMATION_TIME}ms cubic-bezier(0.16, 1, 0.3, 1)`,
          },
          '&[data-state="closed"]': {
            animation: `${slideOutBottom} ${ANIMATION_TIME}ms ease-in forwards`,
          },
        },
      },
      top: {
        '@media (prefers-reduced-motion: no-preference)': {
          '&[data-state="open"]': {
            animation: `${slideInTop} ${ANIMATION_TIME}ms cubic-bezier(0.16, 1, 0.3, 1)`,
          },
          '&[data-state="closed"]': {
            animation: `${slideOutTop} ${ANIMATION_TIME}ms ease-in forwards`,
          },
        },
      },
    },
  },
  pointerEvents: 'initial',
  display: 'inline-block',
  borderRadius: '12px',
  boxShadow: `${theme.shadows.shadow2} $$shadowColor`,
  padding: '16px',
  fontSize: theme.fontSizes.sm,
  '@media (prefers-reduced-motion: no-preference)': {
    '&[data-swipe="move"]': {
      transform: 'translateX(var(--radix-toast-swipe-move-x))',
    },
    '&[data-swipe="cancel"]': {
      transform: 'translateY(0)',
      transition: `transform ${ANIMATION_TIME}ms ease-out`,
    },
    '&[data-swipe="end"]': {
      animation: `${swipeOut} ${ANIMATION_TIME}ms ease-out forwards`,
    },
  },
});

const ToastTitle = styled(ToastPrimitive.Title, {
  variants: {
    color: {
      info: {
        color: theme.colors.blue10,
      },
      success: {
        color: theme.colors.green10,
      },
      danger: {
        color: theme.colors.red10,
      },
      warning: {
        color: theme.colors.yellow11,
      },
    },
  },

  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  fontWeight: theme.fontWeights.medium,
});

const ToastDescription = styled(ToastPrimitive.Description, {
  variants: {
    color: {
      info: {
        color: theme.colors.blue11,
      },
      success: {
        color: theme.colors.green11,
      },
      danger: {
        color: theme.colors.red11,
      },
      warning: {
        color: theme.colors.yellow11,
      },
    },
  },
  fontWeight: theme.fontWeights.normal,
  marginTop: '16px',
});

const ToastViewport = styled(ToastPrimitive.Viewport, {
  display: 'flex',
  justifyContent: 'center',
  position: 'fixed',
  pointerEvents: 'none',
  padding: VIEWPORT_PADDING,
  listStyle: 'none',
  zIndex: 2147483647,

  variants: {
    position: {
      bottom: {
        bottom: 0,
        right: 0,
        left: 0,
        top: 'auto',
      },
      top: {
        top: 0,
        right: 0,
        left: 0,
        bottom: 'auto',
      },
    },
  },
});
